import React, { useState, useEffect, useMemo, useRef } from 'react';
import { base44 } from "@/api/base44Client";
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Send, Loader2, Check, UserCheck, User, X, AlertTriangle, Tv2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useSelectedShow } from "@/hooks/useSelectedShow";
import ShowSelector from "@/components/ShowSelector";
import BadgeScanner from "../components/badge/BadgeScanner";

import PersonPhotoCapture from "../components/badge/PersonPhotoCapture";
import MeetingForm from "../components/meeting/MeetingForm";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/AuthContext';
import { dateToIso, localDate } from '@/components/meeting/MetOnField';
import { formatInZone } from '@/lib/serverTime';
import { timezoneForContact } from '@/lib/showTimezone';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { useNavigationGuard } from '@/hooks/useNavigationGuard';
import { cacheValue, readCachedValue } from '@/lib/offlineCache';
import { queueCapture } from '@/lib/offlineOutbox';
import { sendSmsToContact } from '@/lib/contactSend';
import PageContainer from '@/components/PageContainer';

// A stable reference, not an inline `= []` default: the reps query below is
// permanently disabled (enabled: false) for every non-device-account rep,
// so `data` stays undefined forever for them, and an inline literal default
// would be a brand new array every single render. That fed straight into
// the calendar-links effect's dependency array further down, which depends
// on `reps` by reference, and reproduced a real, confirmed infinite render
// loop (React's "Maximum update depth exceeded") for every ordinary rep,
// caught by scripts/smoke-two-sided-card.mjs, not part of what that script
// was actually testing.
const EMPTY_REPS = [];

export default function NewMeeting() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Generated once per mount, so a rep retrying a failed save (on this same
  // form, whichever button they try) reuses it and POST /api/contacts
  // returns the contact it already made rather than making a twin. A
  // genuinely new capture is a fresh mount of this page (the form has no
  // in-place reset), which gets its own uuid. CLAUDE.md hard rule 4.
  const [clientUuid] = useState(() => crypto.randomUUID());
  const [formData, setFormData] = useState({
    status: 'pending',
    meeting_topics: '',
    // Defaults to today. The rep can move it back to the day they actually met
    // the person, which is what the follow up names.
    captured_at: dateToIso(localDate()),
  });
  const [lowConfidenceFields, setLowConfidenceFields] = useState([]);
  const [duplicateContact, setDuplicateContact] = useState(null);
  // Set only when a photo was taken with no signal to upload it: neither
  // POST /api/badge/scan (which also runs the OCR call) nor POST /api/images
  // can run offline, so the compressed photo itself is held here until the
  // outbox actually reaches the server. Null whenever the online path ran
  // instead, in which case formData.badge_image_key / person_photo_key
  // already carries the real key exactly as before this existed.
  const [badgePhotoBlob, setBadgePhotoBlob] = useState(null);
  const [badgeBackPhotoBlob, setBadgeBackPhotoBlob] = useState(null);
  const [personPhotoBlob, setPersonPhotoBlob] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSendingCalendar, setIsSendingCalendar] = useState(false);
  const [sent, setSent] = useState(false);
  const [sentMode, setSentMode] = useState('calendar'); // 'saved' | 'calendar'
  // Who gets credit for the capture. Defaults to whoever is signed in, and
  // the list comes from the database rather than a hardcoded pair of names.
  const [repId, setRepId] = useState('');
  const [sendEmail, setSendEmail] = useState(false);
  const [sendText, setSendText] = useState(false);
  const [notesPrompt, setNotesPrompt] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // fn to call after notes prompt
  const [notesInput, setNotesInput] = useState('');
  // Shared capture device only: a second check, right before the real send,
  // verifying who this capture actually belongs to. Prefilled with whichever
  // rep is already selected up top, so confirming is one tap in the common
  // case rather than a fresh choice every time.
  const [repConfirmOpen, setRepConfirmOpen] = useState(false);
  const [pendingConfirmRepId, setPendingConfirmRepId] = useState('');
  const [pendingConfirmAction, setPendingConfirmAction] = useState(null);

  // Whether the rep has actually typed or scanned anything worth losing.
  // captured_at and status are set on mount regardless of what the rep does,
  // so they do not count; everything else starts blank.
  const isDirty = !!(
    formData.first_name || formData.last_name || formData.company || formData.title ||
    formData.email || formData.phone || formData.office_phone || formData.website ||
    formData.meeting_topics || formData.internal_notes || formData.badge_image_key ||
    formData.badge_back_image_key || formData.person_photo_key ||
    badgePhotoBlob || badgeBackPhotoBlob || personPhotoBlob
  );
  useUnsavedChangesGuard(isDirty);
  const { setGuard, guardedAction } = useNavigationGuard();
  const goBack = () => guardedAction(() => navigate('/'));

  // Auto-check/uncheck when email or phone fields change
  useEffect(() => {
    setSendEmail(!!formData.email);
  }, [formData.email]);
  useEffect(() => {
    setSendText(!!formData.phone);
  }, [formData.phone]);

  const { selectedShow: activeShow } = useSelectedShow();
  const { user } = useAuth();

  // Shared capture device only (task: shared capture device, 2026-09-08): on
  // a rep's own phone there is nobody to pick, the signed in user always is
  // the rep, so this never even fetches otherwise. GET /api/reps, not
  // base44.entities.User.list(): a device account is a real row but never
  // an admin, and User.list() (GET /api/users) is admin only.
  //
  // Scoped to activeShow (task: per-show rep assignment, 2026-09-15), the
  // same show this capture is about to be filed into (see the useSelectedShow
  // import above), so the picker only offers whoever is assigned to it, or
  // everyone when the show has no assignments. Keyed on activeShow?.id so
  // switching shows on the device re-fetches rather than keeping the
  // previous show's rep list.
  const { data: reps = EMPTY_REPS } = useQuery({
    queryKey: ['reps', activeShow?.id],
    queryFn: async () => {
      try {
        const active = await base44.reps.list(activeShow?.id);
        cacheValue('reps', active);
        return active;
      } catch (err) {
        // Unreachable, not refused: a cold start with no signal at all must
        // still offer a rep to capture as.
        if (err.status === 0) return readCachedValue('reps', []);
        return [];
      }
    },
    enabled: !!user?.is_device_account,
  });

  // A device account is never itself a valid rep to capture as (GET
  // /api/reps already excludes it from the real list), so unlike an
  // ordinary rep it has nothing to fall back to until that list has
  // actually loaded. Memoized: this feeds the effect right below by
  // reference, and an inline `[user]` / `[]` fallback would be a fresh
  // array every render, same footgun EMPTY_REPS above exists to avoid.
  const repOptions = useMemo(
    () => (reps.length > 0 ? reps : user && !user.is_device_account ? [user] : EMPTY_REPS),
    [reps, user],
  );

  // Default to the signed in user once we know who that is, unless this is
  // a device account: there the signed in user is never a valid choice, so
  // this waits for the real rep list and defaults to its first entry
  // instead.
  useEffect(() => {
    if (repId) return;
    if (user?.is_device_account) {
      if (repOptions.length > 0) setRepId(repOptions[0].id);
    } else if (user?.id) {
      setRepId(user.id);
    }
  }, [repId, user, repOptions]);

  // Multi calendar links per rep (task: multi calendar links, 2026-09-07).
  // Whichever rep is scanning, the choice defaults to their own default
  // link and resets whenever the scanning rep changes, so switching who is
  // credited never leaves a stale link from someone else's list selected.
  const currentRep = repOptions.find(r => r.id === repId) || null;
  const repLinks = currentRep?.calendar_links || [];
  useEffect(() => {
    const def = repLinks.find(l => l.is_default) || repLinks[0] || null;
    setFormData(prev => ({ ...prev, calendar_link_id: def?.id || null }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repId, reps]);

  const handleBadgeData = (data) => {
    setLowConfidenceFields(data.low_confidence_fields || []);
    setFormData(prev => ({
      ...prev,
      ...(data.first_name && { first_name: data.first_name }),
      ...(data.last_name && { last_name: data.last_name }),
      ...(data.company && { company: data.company }),
      ...(data.title && { title: data.title }),
      // email_source / phone_source (task: source labels, 2026-09-24): OCR
      // just wrote this field, so it is 'scanned' unless and until
      // MeetingForm.jsx's own update() overwrites it with 'manual' because
      // the rep then edits it by hand. Rescanning re-stamps 'scanned' again,
      // consistent with OCR already overwriting a previous value here.
      ...(data.email && { email: data.email, email_source: 'scanned' }),
      ...(data.phone && { phone: data.phone, phone_source: 'scanned' }),
      ...(data.office_phone && { office_phone: data.office_phone }),
      ...(data.website && { website: data.website }),
    }));
  };

  // These are all R2 object keys, which is what a contact stores. The API
  // validates their shape, so a made up key is refused.
  //
  // badge_ocr_attempted_at: task: deferred badge OCR (2026-09-08). Set here,
  // not derived server side, because this is the one place that knows OCR
  // genuinely ran online just now (BadgeScanner only calls this after a
  // real scan attempt, success or a real OCR failure, never the offline
  // path below). Tells the server not to schedule a second, wasted attempt
  // on a photo that already had one.
  const handleImageCaptured = (key) => {
    setBadgePhotoBlob(null);
    setFormData(prev => ({ ...prev, badge_image_key: key, badge_ocr_attempted_at: new Date().toISOString() }));
  };

  // null means "remove it": BadgeScanner's own X on the back-of-card preview
  // calls this with null, same as it would if the rep had never taken one.
  const handleBackImageCaptured = (key) => {
    setBadgeBackPhotoBlob(null);
    setFormData(prev => ({ ...prev, badge_back_image_key: key }));
  };

  const handlePersonPhoto = (key) => {
    setPersonPhotoBlob(null);
    setFormData(prev => ({ ...prev, person_photo_key: key }));
  };

  // No signal to upload the photo at all, so there is no key yet. The blob
  // travels with the queued capture and is uploaded once the outbox actually
  // reaches the server (offlineOutbox.js's flushOne).
  const handleOfflineBadge = (blob) => {
    setFormData(prev => ({ ...prev, badge_image_key: null }));
    setBadgePhotoBlob(blob);
  };

  const handleOfflineBadgeBack = (blob) => {
    setFormData(prev => ({ ...prev, badge_back_image_key: null }));
    setBadgeBackPhotoBlob(blob);
  };

  const handleOfflinePersonPhoto = (blob) => {
    setFormData(prev => ({ ...prev, person_photo_key: null }));
    setPersonPhotoBlob(blob);
  };

  // Mirrors handleImageCaptured's own badge_ocr_attempted_at stamp, task:
  // Capacitor + on-device OCR before FS/TEC (2026-09-08). BadgeScanner
  // only calls this once on-device OCR genuinely completed (found blocks
  // or not), never on a plugin/infra failure, same reasoning as the
  // online path: this tells the server not to schedule a second, wasted
  // deferred-OCR attempt on a photo that already had one, on-device or
  // online.
  const handleOfflineOcrAttempted = () => {
    setFormData(prev => ({ ...prev, badge_ocr_attempted_at: new Date().toISOString() }));
  };

  // Intercept any save action, and if there are no internal_notes, prompt
  // first. actionWithNotes takes the note text as its one argument (null if
  // skipped) rather than closing over formData.internal_notes: a captured
  // closure created here, before the rep has typed anything, cannot be
  // un-staled later just by waiting for a state update, the same reasoning
  // withRepConfirmation above already gives for repId. This used to set
  // formData via setFormData and then setTimeout(..., 50) before calling the
  // pending action, which reads like it should work but does not: the
  // wrapped save function was already created (and had already closed over
  // the pre-typed formData) back when the button was first clicked, so no
  // amount of waiting made it see the note. Confirmed live: a note typed
  // here saved as null every time, on the one field that records what a
  // conversation was actually about and feeds the follow up emails.
  const withNotesCheck = (actionWithNotes) => {
    if (!formData.internal_notes || !formData.internal_notes.trim()) {
      setNotesInput('');
      setPendingAction(() => actionWithNotes);
      setNotesPrompt(true);
    } else {
      actionWithNotes(formData.internal_notes);
    }
  };

  const confirmNotes = () => {
    const notes = notesInput.trim() || null;
    if (notes) {
      setFormData(prev => ({ ...prev, internal_notes: notes }));
    }
    setNotesPrompt(false);
    const action = pendingAction;
    setPendingAction(null);
    action?.(notes);
  };

  const skipNotes = () => {
    setNotesPrompt(false);
    const action = pendingAction;
    setPendingAction(null);
    action?.(null);
  };

  // Gated on is_device_account, not shown at all on a rep's own phone: there
  // the signed in rep is always themselves, and a popup asking who this is
  // would be friction with no benefit. The outermost check on a save or
  // send, ahead of the notes prompt: verifying who this belongs to comes
  // before anything else about submitting it.
  //
  // actionWithRepId takes the confirmed rep id as its one argument rather
  // than closing over the repId state variable. A setRepId(...) followed by
  // a setTimeout (the pattern confirmNotes below uses for internal_notes)
  // only waits for the state update to commit; it does not fix a callback
  // that already closed over the OLD repId back when the Save button was
  // first clicked, before the popup's own selection ever changed it. Passing
  // the id as a value sidesteps that entirely: there is nothing stale to
  // wait out.
  const withRepConfirmation = (actionWithRepId) => {
    if (!user?.is_device_account) {
      actionWithRepId(repId);
      return;
    }
    setPendingConfirmRepId(repId);
    setPendingConfirmAction(() => actionWithRepId);
    setRepConfirmOpen(true);
  };

  const confirmRep = () => {
    const confirmedRepId = pendingConfirmRepId;
    setRepId(confirmedRepId);
    setRepConfirmOpen(false);
    const action = pendingConfirmAction;
    setPendingConfirmAction(null);
    action?.(confirmedRepId);
  };

  const cancelRepConfirm = () => {
    setRepConfirmOpen(false);
    setPendingConfirmAction(null);
  };

  // The actual save, split from the button's own "Saved!" transition, task:
  // unsaved changes guard, apply to the capture form (2026-09-08). "Save
  // and leave" in the guard dialog needs to run this and know whether it
  // worked, without also showing the 2 second "Contact Saved!" screen,
  // which would just be a confusing detour on the way to wherever the rep
  // was actually trying to go. Registered as this page's onSave, not
  // "Save and Send Calendar Invite": a rep casually navigating away should
  // never have that trigger a real text or email as a side effect.
  // Returns { mode: 'saved', contact } (a real round trip completed),
  // { mode: 'queued' } (no signal, held in the offline outbox instead), or
  // false (a real failure, nothing happened). Both truthy outcomes count as
  // success for the unsaved changes guard and for callers deciding whether
  // to advance the screen: a queued capture is not lost, so it is not
  // treated as a failure. Only status 0 (base44Client.js's ApiError,
  // network unreachable) queues; any other failure (a real validation
  // error, a real 500) still surfaces as an error exactly as before, since
  // queuing that would just retry the same rejection forever.
  //
  // repIdOverride, when given, is the rep just confirmed in the shared
  // device popup (withRepConfirmation); notesOverride, when NOT undefined
  // (a real confirmed value, null included, from withNotesCheck), is what
  // the rep actually typed or explicitly skipped. Both are used instead of
  // reading repId / formData.internal_notes from this closure: see
  // withRepConfirmation's and withNotesCheck's own comments for why a value
  // has to be threaded through explicitly here rather than read from state.
  const performSave = async (repIdOverride, notesOverride) => {
    setIsSaving(true);
    try {
      const effectiveRepId = repIdOverride ?? repId;
      const effectiveNotes = notesOverride !== undefined ? notesOverride : formData.internal_notes;
      const capturedAt = formData.captured_at || new Date().toISOString();
      const showId = activeShow?.id || null;
      const showName = activeShow?.name || formData.trade_show || '';
      const data = { ...formData, internal_notes: effectiveNotes, client_uuid: clientUuid, status: 'saved', scanned_by: effectiveRepId, captured_at: capturedAt, trade_show_id: showId, trade_show: showName };

      try {
        const res = await base44.functions.invoke('createMeeting', { data });
        // Home's own list query has staleTime 0 and refetches on every
        // mount regardless, so this is belt and suspenders, not the only
        // thing standing between a save and a rep seeing it: it makes sure
        // the request is already in flight before navigation happens, so
        // there is no gap where a fast rep lands on Home before the fresh
        // fetch was even asked for.
        queryClient.invalidateQueries({ queryKey: ['meetings'] });
        return { mode: 'saved', contact: res.data || res };
      } catch (err) {
        if (err.status === 0) {
          await queueCapture({
            clientUuid,
            payload: data,
            badgeBlob: badgePhotoBlob,
            badgeBackBlob: badgeBackPhotoBlob,
            personBlob: personPhotoBlob,
          });
          return { mode: 'queued' };
        }
        throw err;
      }
    } catch (err) {
      toast.error(err.message || "Failed to save contact. Please try again.");
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const saveContactOnly = async (repIdOverride, notesOverride) => {
    const result = await performSave(repIdOverride, notesOverride);
    if (result) {
      setSentMode(result.mode === 'queued' ? 'queued' : 'saved');
      setSent(true);
      setTimeout(() => navigate('/'), 2000);
    }
  };

  // Registers this page's dirty state and save function with the global
  // guard (App.jsx's AppShellLayout), so the bottom nav's Home/+/More can
  // catch an abandoned capture too, not just this page's own back arrow.
  useEffect(() => {
    setGuard(isDirty, performSave);
    return () => setGuard(false, null);
  });

  const saveAndSendCalendar = async (mode = 'both', repIdOverride, notesOverride) => {
    setIsSendingCalendar(true);
    try {
      const result = await performSave(repIdOverride, notesOverride);
      if (!result) return;

      // No signal at all: the contact is safely queued (performSave already
      // handled that), but there is nothing to send it to yet, and no
      // durable "pending send" exists to queue the send itself into. The rep
      // finishes this from the contact's own detail page once they are back
      // online, where Send Email / Send SMS already exist unchanged. Losing
      // a scheduled send is recoverable there; losing the contact itself is
      // not, which is the one thing this must never do.
      if (result.mode === 'queued') {
        toast.warning("You're offline. The contact is saved and will sync once you're back online, from where you can send the calendar invite.");
        setSentMode('queued');
        setSent(true);
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      const created = result.contact;

      // Task 3.3's real send routes, one call per checked channel, so a
      // Twilio or Google failure on one does not also swallow the other
      // succeeding, and the contact this just created is never left with no
      // record of what actually went out.
      //
      // No cross-owner confirmation here (task: rework how the welcome text
      // and email are sent, 2026-09-23), unlike EditContact.jsx's own two
      // send buttons: a contact created here is always owned by exactly
      // whoever withRepConfirmation above just confirmed, and that rep
      // picker only ever appears for the shared device account (line ~520),
      // whose own login can never itself be a contact's owner anyway
      // (worker/routes/reps.ts excludes device accounts from the pickable
      // rep list). A plain rep's own login has no rep picker at all: repId
      // always defaults to, and stays, their own user id. So there is no
      // way to be logged in here as one real rep and have this create a
      // contact owned by a different one; the cross-owner case this
      // confirmation exists for cannot occur on this screen.
      const attempts = [
        mode !== 'text' && { label: 'email', run: () => base44.outbound.sendEmail(created.id) },
        mode !== 'email' && { label: 'text', run: () => sendSmsToContact(user, created) },
      ].filter(Boolean);

      const outcomes = await Promise.all(
        attempts.map(async ({ label, run }) => {
          try {
            await run();
            return { label, ok: true };
          } catch (err) {
            return { label, ok: false, message: err.message || `Failed to send the ${label}.` };
          }
        }),
      );

      const succeeded = outcomes.filter((o) => o.ok);
      const failed = outcomes.filter((o) => !o.ok);

      for (const failure of failed) toast.error(failure.message);

      if (succeeded.length) {
        // The contact is saved either way; only advance to the "sent" screen
        // when something actually went out, so a full failure leaves the rep
        // looking at the form rather than believing this worked.
        setSentMode('calendar');
        setSent(true);
        setTimeout(() => navigate('/'), 2000);
      }
    } catch (err) {
      toast.error(err.message || "Failed to send. Please try again.");
    } finally {
      setIsSendingCalendar(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center"
        >
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="text-xl font-bold text-foreground mb-1">
            {sentMode === 'queued' ? 'Saved, Will Sync Later' : sentMode === 'saved' ? 'Contact Saved!' : 'Request Sent!'}
          </h2>
          <p className="text-muted-foreground text-sm">
            {/* formData.first_name is never initialized in state at all (only
                set if the rep types it or OCR fills it), so a nameless
                offline capture left it as JS undefined, not '', which a
                template literal renders as the literal string "undefined".
                Confirmed live: "undefined is saved on this phone...". Never
                interpolate the raw field; fall back the same way
                ContactRow.jsx and EditContact.jsx already do. */}
            {sentMode === 'queued'
              ? `${formData.first_name?.trim() || 'This contact'} is saved on this phone and will sync once you're back online.`
              : sentMode === 'saved'
                ? `${formData.first_name?.trim() || 'This contact'} has been saved.`
                : `Request sent asking ${formData.first_name?.trim() || 'this contact'} to schedule a meeting via ${[sendEmail && 'email', sendText && 'text'].filter(Boolean).join(' and ')}.`
            }
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <>
    <div className="min-h-screen bg-background safe-top pb-24">
      <PageContainer className="px-4 py-6">
        {/* Header. Global "Back" button (task: 2026-09-25), replacing the
            old round arrow icon, same visual style as the Cancel button
            elsewhere in this app. */}
        <div className="flex items-center gap-3 mb-6">
          <Button variant="outline" onClick={goBack} className="min-h-9 px-4 text-sm">Back</Button>
          <div>
            <h1 className="text-xl font-bold text-foreground">New Contact</h1>
            <p className="text-sm text-muted-foreground">Scan badge and schedule meeting</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Rep Picker, shared capture device only: on a rep's own phone the
              signed in user always is the rep, nothing to pick. */}
          {user?.is_device_account && (
            <div className="bg-card rounded-2xl p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-accent/10 flex items-center justify-center shrink-0">
                  <User className="w-5 h-5 text-accent" />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Scanning As</p>
                  <Select value={repId} onValueChange={setRepId}>
                    <SelectTrigger className="h-10 bg-secondary/50 border-0 focus-visible:ring-accent font-semibold">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {repOptions.map(rep => (
                        <SelectItem key={rep.id} value={rep.id}>
                          <span className="flex items-center gap-1.5">
                            {rep.full_name || rep.email}
                            {rep.google_status && rep.google_status !== 'connected' && (
                              <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                            )}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

        {/* Event Selector, task: remove the event dropdown for a shared
            device account, 2026-09-16. Confirmed with the owner: a shared
            device is set up once per show and never needs to switch events
            mid session from this screen (a second device for a second
            booth would be its own separate "Shared Account", not this one
            switching back and forth), so letting it change here was a
            control that should not exist for it at all. Still reads
            activeShow, the exact same value ShowSelector itself reads
            (useSelectedShow), unchanged: only editability moves, not where
            the show actually comes from, which stays the Shows screen in
            the main menu either way. Shown as plain read-only text rather
            than omitted outright, since the rep doing the scanning still
            benefits from seeing which show a capture is about to be filed
            into. A regular rep's own capture screen is completely
            untouched, still the real, editable ShowSelector exactly as
            before. */}
          {user?.is_device_account ? (
            <div className="flex items-center gap-2.5 px-4 py-2.5 bg-card border border-border rounded-xl">
              <Tv2 className="w-4 h-4 text-accent shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground font-medium">Current Event</p>
                <p className="text-sm font-semibold text-foreground truncate">
                  {activeShow
                    ? `${activeShow.name}${activeShow.location ? ` · ${activeShow.location}` : ''}`
                    : 'No event set'}
                </p>
              </div>
            </div>
          ) : (
            <ShowSelector />
          )}

        {/* Badge Scanner */}
          <div className="bg-card rounded-2xl p-5 shadow-sm">
            <BadgeScanner
              onDataExtracted={handleBadgeData}
              onImageCaptured={handleImageCaptured}
              onBackImageCaptured={handleBackImageCaptured}
              onDuplicateFound={setDuplicateContact}
              onOfflineCapture={handleOfflineBadge}
              onOfflineBackCapture={handleOfflineBadgeBack}
              onOfflineOcrAttempted={handleOfflineOcrAttempted}
            />
          </div>

          {/* Duplicate Warning */}
          {duplicateContact && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 bg-amber-50 border border-amber-300 rounded-2xl"
            >
              <div className="flex items-start gap-3 mb-3">
                <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-amber-800">Duplicate Contact Detected</p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    <span className="font-medium">{duplicateContact.first_name} {duplicateContact.last_name}</span>
                    {duplicateContact.company ? ` from ${duplicateContact.company}` : ''}
                    {' '}was already captured
                    {duplicateContact.captured_at
                      ? ` on ${formatInZone(duplicateContact.captured_at, timezoneForContact(duplicateContact), "EEE MMM d, h:mm a zzz")}`
                      : ''}.
                  </p>
                </div>
                <button onClick={() => setDuplicateContact(null)} className="text-amber-400 hover:text-amber-600 shrink-0">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/')}
                  className="flex-1 h-9 text-xs font-semibold border-amber-400 text-amber-800 hover:bg-amber-100"
                >
                  Delete & Rescan
                </Button>
                <Button
                  size="sm"
                  onClick={() => setDuplicateContact(null)}
                  className="flex-1 h-9 text-xs font-semibold bg-amber-500 hover:bg-amber-600 text-white"
                >
                  Save Contact Again
                </Button>
              </div>
            </motion.div>
          )}

          {/* Person Photo */}
          <div className="bg-card rounded-2xl p-5 shadow-sm">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">Person Photo (Optional)</p>
            <PersonPhotoCapture onPhotoCaptured={handlePersonPhoto} onOfflineCapture={handleOfflinePersonPhoto} />
          </div>

          {/* Form */}
          <div className="bg-card rounded-2xl p-5 shadow-sm">
            <MeetingForm
              show={activeShow}
              formData={formData}
              setFormData={setFormData}
              lowConfidenceFields={lowConfidenceFields}
              onSaveContact={saveContactOnly}
              isSaving={isSaving}
              repId={repId}
            />
          </div>

          {/* Save Contact Only Button */}
          <Button
            onClick={() => withRepConfirmation((confirmedRepId) => withNotesCheck((notes) => saveContactOnly(confirmedRepId, notes)))}
            disabled={isSaving}
            variant="outline"
            className="w-full h-11 text-sm font-semibold rounded-xl border-2"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <UserCheck className="w-4 h-4 mr-2" />
                Save Contact W/O Sending Anything
              </>
            )}
          </Button>

          {/* Send Calendar Link Button with checkboxes */}
          <div className="space-y-4">
            {/* Which of the rep's calendar links this contact gets. Only
                shown when there is an actual choice to make; a rep with one
                link or none just uses it silently, nothing to switch. */}
            {repLinks.length > 1 && (
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-medium text-muted-foreground">Calendar link</span>
                <Select
                  value={formData.calendar_link_id || ''}
                  onValueChange={v => setFormData(prev => ({ ...prev, calendar_link_id: v }))}
                >
                  <SelectTrigger className="h-8 w-44 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {repLinks.map(l => (
                      <SelectItem key={l.id} value={l.id}>
                        {l.name}{l.is_default ? ' (default)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex items-center justify-end gap-6">
              <label className={`flex items-center gap-2 cursor-pointer select-none ${!formData.phone ? 'opacity-40 cursor-not-allowed' : ''}`}>
                <span className="text-sm font-medium text-foreground">Text</span>
                <input
                  type="checkbox"
                  checked={sendText && !!formData.phone}
                  onChange={e => formData.phone && setSendText(e.target.checked)}
                  disabled={!formData.phone}
                  className="w-5 h-5 accent-amber-500 cursor-pointer"
                />
              </label>
              <label className={`flex items-center gap-2 cursor-pointer select-none ${!formData.email ? 'opacity-40 cursor-not-allowed' : ''}`}>
                <span className="text-sm font-medium text-foreground">Email</span>
                <input
                  type="checkbox"
                  checked={sendEmail && !!formData.email}
                  onChange={e => formData.email && setSendEmail(e.target.checked)}
                  disabled={!formData.email}
                  className="w-5 h-5 accent-amber-500 cursor-pointer"
                />
              </label>
            </div>
            <Button
              onClick={() => {
                withRepConfirmation((confirmedRepId) => {
                  withNotesCheck((notes) => {
                    if (sendEmail && sendText) saveAndSendCalendar('both', confirmedRepId, notes);
                    else if (sendEmail) saveAndSendCalendar('email', confirmedRepId, notes);
                    else if (sendText) saveAndSendCalendar('text', confirmedRepId, notes);
                  });
                });
              }}
              disabled={isSendingCalendar || (!sendEmail && !sendText)}
              className={`w-full h-11 text-sm font-semibold rounded-xl transition-colors ${(!sendEmail && !sendText) ? 'bg-secondary/40 text-muted-foreground cursor-not-allowed' : 'bg-accent hover:bg-accent/90 text-accent-foreground'}`}
            >
              {isSendingCalendar ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
              ) : (
                <><Send className="w-5 h-5 mr-2" />
                  {(!sendEmail && !sendText) ? 'Send Scheduling Invitation' :
                   (sendEmail && sendText) ? 'Send Text and Email: Request a Meeting' :
                   sendEmail ? 'Send Email: Request a Meeting' :
                   'Send Text: Request a Meeting'}
                </>
              )}
            </Button>
          </div>

          <div className="h-8" />
        </div>
      </PageContainer>
    </div>

    {/* Rep Confirmation, shared capture device only */}
    <AnimatePresence key="rep-confirm-prompt">
      {repConfirmOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-6"
        >
          <motion.div
            initial={{ scale: 0.92, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.92, opacity: 0 }}
            className="bg-card rounded-2xl shadow-2xl w-full max-w-sm p-6"
          >
            <h3 className="text-lg font-bold text-foreground mb-1">Who is this contact for?</h3>
            <p className="text-sm text-muted-foreground mb-4">Confirm the rep before this saves.</p>
            <Select value={pendingConfirmRepId} onValueChange={setPendingConfirmRepId}>
              <SelectTrigger className="h-10 mb-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {repOptions.map(rep => (
                  <SelectItem key={rep.id} value={rep.id}>
                    <span className="flex items-center gap-1.5">
                      {rep.full_name || rep.email}
                      {rep.google_status && rep.google_status !== 'connected' && (
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                      )}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(() => {
              const pendingRep = repOptions.find(r => r.id === pendingConfirmRepId);
              return pendingRep?.google_status && pendingRep.google_status !== 'connected' ? (
                <p className="text-xs text-amber-600 mb-4 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                  {pendingRep.full_name || pendingRep.email} has not connected Google. Email sends will fail until they do.
                </p>
              ) : <div className="mb-4" />;
            })()}
            <div className="flex gap-3">
              <Button variant="outline" onClick={cancelRepConfirm} className="flex-1 min-h-10 text-sm">
                Cancel
              </Button>
              <Button
                onClick={confirmRep}
                className="flex-1 min-h-10 text-sm bg-accent hover:bg-accent/90 text-accent-foreground font-semibold"
              >
                Confirm & Continue
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>

    {/* Internal Notes Prompt */}
    <AnimatePresence key="notes-prompt">
      {notesPrompt && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          // items-start with top padding, not items-center (task:
          // keyboard-safe modals, 2026-09-08): centered in the layout
          // viewport, which does not shrink when the on-screen keyboard
          // opens, this modal's own Save/Skip buttons could end up behind
          // the keyboard. Anchoring near the top keeps them clear of it.
          className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-6 pt-16 overflow-y-auto"
        >
          <motion.div
            initial={{ scale: 0.92, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.92, opacity: 0 }}
            className="bg-card rounded-2xl shadow-2xl w-full max-w-sm p-6 max-h-[80vh] overflow-y-auto"
          >
            <h3 className="text-lg font-bold text-foreground mb-1">Add Internal Notes?</h3>
            <p className="text-sm text-muted-foreground mb-4">You didn't add any private notes for this contact. Add them now or skip to save without notes.</p>
            <Textarea
              placeholder="e.g. 150-room hotel, quoted $499/mo, very interested…"
              value={notesInput}
              onChange={e => setNotesInput(e.target.value)}
              className="mb-4 h-28 resize-none"
              autoFocus
            />
            <div className="flex gap-3">
              <Button variant="outline" onClick={skipNotes} className="flex-1 min-h-10 text-sm">
                Skip
              </Button>
              <Button
                onClick={confirmNotes}
                className="flex-1 min-h-10 text-sm bg-accent hover:bg-accent/90 text-accent-foreground font-semibold"
              >
                Save Notes & Continue
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
    </>
  );
}